import './Reports.css';
import api from '../api.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { downloadExcel } from '../utils/exportExcel.js';
import { fmtInt, fmtMoney, fmtDateTime, fmtDate, localISODate } from '../utils/format';

const REPORT_TYPES = [
    { key: 'messages', label: 'Сообщения' },
    { key: 'daily', label: 'Статистика по дням' },
    { key: 'balance', label: 'Баланс' },
    { key: 'campaigns', label: 'Рассылки' },
];

const STATUS_LABELS = {
    sent: 'Отправлено',
    delivered: 'Доставлено',
    failed: 'Не отправлено',
    enqueued: 'В очереди',
    read: 'Прочитано',
};

const formatDateTime = fmtDateTime;
const formatDate = fmtDate;
const formatCost = (value) => Number(value || 0).toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

// Итоги в деньгах («Сумма», «Пополнения», «Списания») пока скрыты у всех (решение 28.10.2026);
// построчные суммы в выгрузках остаются.
const SHOW_MONEY_TOTALS = false;

const inDateRange = (value, from, to) => {
    if (!value) return false;
    const date = value.split('T')[0];
    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
};

export default function Reports() {
    const me = JSON.parse(localStorage.getItem('me') || '{}');
    const role = me?.userInfo?.role;
    const myPartnerName = me?.userInfo?.partnerName;

    const today = localISODate();
    const yearStart = `${today.slice(0, 4)}-01-01`;

    const [reportType, setReportType] = useState('messages');
    const [partners, setPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState('');
    const [fromDate, setFromDate] = useState(yearStart);
    const [toDate, setToDate] = useState(today);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (role === 'Админ') {
            api.get('/api/partners/list')
                .then((res) => setPartners(res.data || []))
                .catch(() => setPartners([]));
        }
    }, [role]);

    const partnerForRequest = role === 'Партнер' ? myPartnerName : selectedPartner;

    const summary = useMemo(() => {
        if (!rows.length) {
            return { count: 0, totalCost: 0, sent: 0, delivered: 0, errors: 0 };
        }

        if (reportType === 'messages') {
            return {
                count: rows.length,
                totalCost: rows.reduce((sum, row) => sum + Number(row.price || 0), 0),
            };
        }

        if (reportType === 'daily') {
            return {
                count: rows.length,
                sent: rows.reduce((sum, row) => sum + Number(row.sent || 0), 0),
                delivered: rows.reduce((sum, row) => sum + Number(row.delivered || 0), 0),
                errors: rows.reduce((sum, row) => sum + Number(row.error || 0), 0),
            };
        }

        if (reportType === 'balance') {
            const topUps = rows.filter((row) => row.changeType === 'Пополнение');
            const charges = rows.filter((row) => row.changeType !== 'Пополнение');
            return {
                count: rows.length,
                topUps: topUps.reduce((sum, row) => sum + Number(row.amount || 0), 0),
                charges: charges.reduce((sum, row) => sum + Number(row.amount || 0), 0),
            };
        }

        if (reportType === 'campaigns') {
            return {
                count: rows.length,
                recipients: rows.reduce((sum, row) => sum + Number(row.recipientCount || 0), 0),
                sent: rows.reduce((sum, row) => sum + Number(row.sentCount || 0), 0),
                errors: rows.reduce((sum, row) => sum + Number(row.errorCount || 0), 0),
            };
        }

        return { count: rows.length };
    }, [rows, reportType]);

    const buildDateParams = () => {
        const params = {};
        if (fromDate) params.from = fromDate;
        if (toDate) params.to = toDate;
        return params;
    };

    const loadReport = useCallback(async () => {
        setLoading(true);
        setError('');
        setLoaded(false);

        try {
            if (fromDate && toDate && fromDate > toDate) {
                setError('Дата «С» не может быть позже даты «По»');
                setRows([]);
                return;
            }

            if (reportType === 'balance' && role === 'Админ' && !partnerForRequest) {
                setError('Выберите партнёра для отчёта по балансу');
                setRows([]);
                return;
            }

            let data = [];

            if (reportType === 'messages') {
                const params = buildDateParams();
                if (partnerForRequest) params.partnerName = partnerForRequest;
                const res = await api.get('/api/messages/allMessages', { params });
                data = res.data || [];
            }

            if (reportType === 'daily') {
                const params = buildDateParams();
                if (partnerForRequest) params.partnerName = partnerForRequest;
                const res = await api.get('/api/messages/stats', { params });
                data = res.data || [];
            }

            if (reportType === 'balance') {
                const params = { ...buildDateParams() };
                if (partnerForRequest) params.partnerName = partnerForRequest;
                const res = await api.get('/api/balance/balance-history', { params });
                data = res.data || [];
            }

            if (reportType === 'campaigns') {
                const params = {};
                if (partnerForRequest) params.partnerName = partnerForRequest;
                const res = await api.get('/api/campaigns/list', { params });
                data = (res.data || []).filter((item) => {
                    if (!fromDate && !toDate) return true;
                    return inDateRange(item.createdAt, fromDate || '1970-01-01', toDate || '2999-12-31');
                });
            }

            setRows(data);
            setLoaded(true);
        } catch (err) {
            setRows([]);
            const apiError = err?.response?.data;
            setError(typeof apiError === 'string' ? apiError : 'Ошибка загрузки отчёта');
        } finally {
            setLoading(false);
        }
    }, [reportType, fromDate, toDate, partnerForRequest, role]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const exportReport = () => {
        if (!rows.length) return;

        const stamp = new Date().toISOString().slice(0, 10);

        if (reportType === 'messages') {
            downloadExcel({
                sheetName: 'Сообщения',
                headers: ['Партнёр', 'Номер', 'Статус', 'Дата', 'Цена', 'История статусов'],
                rows: rows.map((row) => [
                    row.partner,
                    row.destination,
                    STATUS_LABELS[row.status] || row.status,
                    formatDateTime(row.date),
                    row.price ?? '',
                    (row.history || [])
                        .map((item) => `${STATUS_LABELS[item.status] || item.status} (${formatDateTime(item.date)})`)
                        .join('; '),
                ]),
                fileName: `wave_messages_${stamp}.xlsx`,
            });
            return;
        }

        if (reportType === 'daily') {
            downloadExcel({
                sheetName: 'Статистика',
                headers: ['Дата', 'Партнёр', 'Отправлено', 'Доставлено', 'Ошибки'],
                rows: rows.map((row) => [
                    row.date,
                    row.partnerName,
                    row.sent,
                    row.delivered,
                    row.error ?? 0,
                ]),
                fileName: `wave_daily_stats_${stamp}.xlsx`,
            });
            return;
        }

        if (reportType === 'balance') {
            downloadExcel({
                sheetName: 'Баланс',
                headers: ['Тип операции', 'Сумма', 'Дата и время'],
                rows: rows.map((row) => [
                    row.changeType,
                    row.amount,
                    formatDateTime(row.dateTime),
                ]),
                fileName: `wave_balance_${stamp}.xlsx`,
            });
            return;
        }

        if (reportType === 'campaigns') {
            downloadExcel({
                sheetName: 'Рассылки',
                headers: [
                    'Партнёр', 'Название', 'Шаблон', 'Статус',
                    'Получателей', 'Отправлено', 'Ошибок',
                    'Создана', 'Запущена', 'Завершена',
                ],
                rows: rows.map((row) => [
                    row.partner,
                    row.name,
                    row.templateName,
                    row.status,
                    row.recipientCount,
                    row.sentCount,
                    row.errorCount,
                    formatDateTime(row.createdAt),
                    formatDateTime(row.startedAt),
                    formatDateTime(row.completedAt),
                ]),
                fileName: `wave_campaigns_${stamp}.xlsx`,
            });
        }
    };

    const renderTable = () => {
        if (loading) {
            return <div className="reports-empty">Загрузка...</div>;
        }

        if (error) {
            return <div className="reports-error">{error}</div>;
        }

        if (!rows.length) {
            const hints = {
                messages: 'Сообщений за выбранный период нет. Проверьте даты или откройте вкладку «Сообщения».',
                daily: 'Статистика по дням пуста. Данные появляются после отправки WhatsApp-сообщений.',
                balance: 'Операций по балансу за период нет.',
                campaigns: 'Рассылок за выбранный период нет.',
            };
            return <div className="reports-empty">{hints[reportType]}</div>;
        }

        if (reportType === 'messages') {
            return (
                <table className="reports-table">
                    <thead>
                        <tr>
                            <th>Партнёр</th>
                            <th>Номер</th>
                            <th>Статус</th>
                            <th>Дата</th>
                            <th>Цена</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={`${row.destination}-${row.date}-${index}`}>
                                <td>{row.partner}</td>
                                <td>{row.destination}</td>
                                <td>{STATUS_LABELS[row.status] || row.status}</td>
                                <td className="nowrap">{formatDateTime(row.date)}</td>
                                <td className="num">{fmtMoney(row.price)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        if (reportType === 'daily') {
            return (
                <table className="reports-table">
                    <thead>
                        <tr>
                            <th>Дата</th>
                            <th>Партнёр</th>
                            <th>Отправлено</th>
                            <th>Доставлено</th>
                            <th>Ошибки</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={`${row.partnerName}-${row.date}-${index}`}>
                                <td className="nowrap">{formatDate(row.date)}</td>
                                <td>{row.partnerName}</td>
                                <td className="num">{fmtInt(row.sent)}</td>
                                <td className="num">{fmtInt(row.delivered)}</td>
                                <td className="num">{fmtInt(row.error ?? 0)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        if (reportType === 'balance') {
            return (
                <table className="reports-table">
                    <thead>
                        <tr>
                            <th>Тип операции</th>
                            <th>Сумма</th>
                            <th>Дата и время</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={`${row.changeType}-${row.dateTime}-${index}`}>
                                <td>{row.changeType}</td>
                                <td className="num">{formatCost(row.amount)}</td>
                                <td className="nowrap">{formatDateTime(row.dateTime)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        return (
            <table className="reports-table">
                <thead>
                    <tr>
                        <th>Партнёр</th>
                        <th>Название</th>
                        <th>Шаблон</th>
                        <th>Статус</th>
                        <th>Получателей</th>
                        <th>Отправлено</th>
                        <th>Ошибок</th>
                        <th>Создана</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.id}>
                            <td>{row.partner}</td>
                            <td>{row.name}</td>
                            <td>{row.templateName}</td>
                            <td>{row.status}</td>
                            <td className="num">{fmtInt(row.recipientCount)}</td>
                            <td className="num">{fmtInt(row.sentCount)}</td>
                            <td className="num">{fmtInt(row.errorCount)}</td>
                            <td className="nowrap">{formatDateTime(row.createdAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderSummary = () => {
        if (!loaded || !rows.length) return null;

        if (reportType === 'messages') {
            return (
                <div className="reports-summary">
                    <div className="reports-summary-card">
                        <p>Сообщений</p>
                        <b>{fmtInt(summary.count)}</b>
                    </div>
                    {SHOW_MONEY_TOTALS && (
                        <div className="reports-summary-card">
                            <p>Сумма</p>
                            <b>{formatCost(summary.totalCost)}</b>
                        </div>
                    )}
                </div>
            );
        }

        if (reportType === 'daily') {
            return (
                <div className="reports-summary">
                    <div className="reports-summary-card">
                        <p>Записей</p>
                        <b>{fmtInt(summary.count)}</b>
                    </div>
                    <div className="reports-summary-card">
                        <p>Отправлено</p>
                        <b>{fmtInt(summary.sent)}</b>
                    </div>
                    <div className="reports-summary-card">
                        <p>Доставлено</p>
                        <b>{fmtInt(summary.delivered)}</b>
                    </div>
                    <div className="reports-summary-card">
                        <p>Ошибки</p>
                        <b>{fmtInt(summary.errors)}</b>
                    </div>
                </div>
            );
        }

        if (reportType === 'balance') {
            return (
                <div className="reports-summary">
                    <div className="reports-summary-card">
                        <p>Операций</p>
                        <b>{fmtInt(summary.count)}</b>
                    </div>
                    {SHOW_MONEY_TOTALS && (
                        <>
                            <div className="reports-summary-card">
                                <p>Пополнения</p>
                                <b>{formatCost(summary.topUps)}</b>
                            </div>
                            <div className="reports-summary-card">
                                <p>Списания</p>
                                <b>{formatCost(summary.charges)}</b>
                            </div>
                        </>
                    )}
                </div>
            );
        }

        return (
            <div className="reports-summary">
                <div className="reports-summary-card">
                    <p>Рассылок</p>
                    <b>{fmtInt(summary.count)}</b>
                </div>
                <div className="reports-summary-card">
                    <p>Получателей</p>
                    <b>{fmtInt(summary.recipients)}</b>
                </div>
                <div className="reports-summary-card">
                    <p>Отправлено</p>
                    <b>{fmtInt(summary.sent)}</b>
                </div>
                <div className="reports-summary-card">
                    <p>Ошибок</p>
                    <b>{fmtInt(summary.errors)}</b>
                </div>
            </div>
        );
    };

    return (
        <div className="reports-main">
            <div className="reports-header">
                <div>
                    <p className="reports-subtitle">
                        Сформируйте отчёт по сообщениям, статистике, балансу или рассылкам и скачайте в Excel.
                    </p>
                </div>
            </div>

            <div className="segs reports-type-tabs" role="tablist">
                {REPORT_TYPES.map((type) => (
                    <button
                        key={type.key}
                        type="button"
                        role="tab"
                        aria-selected={reportType === type.key}
                        className={'seg' + (reportType === type.key ? ' active' : '')}
                        onClick={() => {
                            setReportType(type.key);
                            setError('');
                        }}
                    >
                        {type.label}
                    </button>
                ))}
            </div>

            <div className="reports-filters">
                <label>
                    С
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </label>
                <label>
                    По
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </label>

                {role === 'Админ' && (
                    <label>
                        Партнёр
                        <select
                            value={selectedPartner}
                            onChange={(e) => setSelectedPartner(e.target.value)}
                        >
                            <option value="">
                                {reportType === 'balance' ? 'Выберите партнёра' : 'Все партнёры'}
                            </option>
                            {partners.map((partner) => (
                                <option key={partner.id} value={partner.name}>
                                    {partner.name}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                <div className="reports-actions">
                    <button type="button" className="reports-load-btn" onClick={loadReport} disabled={loading}>
                        {loading ? 'Загрузка...' : 'Сформировать'}
                    </button>
                    <button
                        type="button"
                        className="reports-export-btn"
                        onClick={exportReport}
                        disabled={!rows.length || loading}
                    >
                        Скачать Excel
                    </button>
                </div>
            </div>

            {renderSummary()}

            <div className="reports-table-wrapper">
                {renderTable()}
            </div>
        </div>
    );
}
